import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, DollarSign, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { PurchaseOrder } from '@/lib/types/job';
import { formatCurrency } from '@/lib/types/job';

interface FinancialAdjustmentFormProps {
  purchaseOrder: PurchaseOrder;
  userRole: 'client' | 'vendor';
  onSubmitted: () => void;
  onCancel: () => void;
}

type AdjustmentType = 'credit' | 'debit';

export function FinancialAdjustmentForm({
  purchaseOrder,
  userRole,
  onSubmitted,
  onCancel,
}: FinancialAdjustmentFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  
  // Fetch platform fee percentage for vendor payout preview
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');
  const platformFeePercentage = parseFloat(platformFeeSetting?.setting_value || '7');
  
  // Check if associated job is finalized (fetch job status)
  const [isJobFinalized, setIsJobFinalized] = useState(false);
  
  useEffect(() => {
    const checkJobStatus = async () => {
      if (!purchaseOrder.job_id) return;
      
      const { data: job } = await supabase
        .from('jobs')
        .select('status')
        .eq('id', purchaseOrder.job_id)
        .single();
      
      if (job && ['accepted', 'rejected', 'closed'].includes(job.status)) {
        setIsJobFinalized(true);
      }
    };
    
    checkJobStatus();
  }, [purchaseOrder.job_id]);
  
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('credit');
  const [amountDollars, setAmountDollars] = useState('');
  const [reason, setReason] = useState('');

  // Calculate adjustment details
  const amountCents = Math.round(parseFloat(amountDollars || '0') * 100);
  const adjustmentAmountCents = adjustmentType === 'credit' ? -amountCents : amountCents;
  
  // Calculate tax-inclusive current PO amount
  const taxAmount = (purchaseOrder as any).bid?.total_vendor_tax_cents || 0;
  const currentPOWithTax = purchaseOrder.po_amount_cents + taxAmount;
  const newTotal = currentPOWithTax + adjustmentAmountCents;
  
  // Calculate vendor payout preview for debit adjustments
  const platformFeeCents = adjustmentType === 'debit' && amountCents > 0
    ? Math.floor(amountCents * (platformFeePercentage / 100))
    : 0;
  const vendorPayoutCents = amountCents - platformFeeCents;
  
  // All adjustments now require approval from the other party
  const willAutoApprove = false;
  const requiresApproval = true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isJobFinalized) {
      toast({
        title: 'Job Finalized',
        description: 'Cannot submit change orders for finalized jobs',
        variant: 'destructive',
      });
      return;
    }
    
    if (!user || amountCents <= 0 || !reason.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      // Insert adjustment request
      const { error: insertError } = await supabase
        .from('po_modification_requests')
        .insert({
          purchase_order_id: purchaseOrder.id,
          request_type: 'price_change',
          requested_by_user_id: user.id,
          request_details: {
            adjustment_type: adjustmentType,
            amount_dollars: parseFloat(amountDollars),
          },
          adjustment_amount_cents: adjustmentAmountCents,
          adjustment_reason: reason.trim(),
          initiating_party: userRole,
          requires_approval: requiresApproval,
          auto_approved: willAutoApprove,
          status: willAutoApprove ? 'accepted' : 'pending',
        });

      if (insertError) throw insertError;

      // Send notification for adjustments requiring approval
      const { error: notifyError } = await invokeEdgeFunction('send-po-notification', {
        body: {
          purchaseOrderId: purchaseOrder.id,
          notificationType: 'adjustment_requested',
          adjustmentData: {
            amount_cents: adjustmentAmountCents,
            adjustment_type: adjustmentType,
            reason: reason.trim(),
            initiating_party: userRole,
            requestedBy: user?.id,
          },
        },
        callerOperation: 'FinancialAdjustmentForm.handleSubmit',
      });

      if (notifyError) {
        console.error('Error sending adjustment notification:', notifyError);
      }

      toast({
        title: 'Change Order Requested',
        description: `Your change order request has been sent for ${userRole === 'client' ? 'vendor' : 'client'} approval.`,
      });

      onSubmitted();
    } catch (error) {
      console.error('Error submitting adjustment:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit change order request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Show read-only alert if job is finalized
  if (isJobFinalized) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          This job has been finalized and is now read-only. No further change orders can be requested.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tax Note */}
      <Alert variant="default" className="bg-muted/50 border-muted-foreground/20">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-sm">
          Tax is not automatically calculated for change orders. If this adjustment affects the tax amount, include the tax difference in your total adjustment.
        </AlertDescription>
      </Alert>
      {/* Change Order Type */}
      <div className="space-y-3">
        <Label>Change Order Type</Label>
        <RadioGroup value={adjustmentType} onValueChange={(val) => setAdjustmentType(val as AdjustmentType)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="credit" id="credit" />
            <Label htmlFor="credit" className="font-normal cursor-pointer">
              Credit (reduce amount)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="debit" id="debit" />
            <Label htmlFor="debit" className="font-normal cursor-pointer">
              Debit (increase amount)
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <Label htmlFor="amount">Amount ($)</Label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            placeholder="0.00"
            className="pl-9"
            required
          />
        </div>
      </div>

      {/* Reason */}
      <div className="space-y-2">
        <Label htmlFor="reason">Reason for Change Order</Label>
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this change order is needed..."
          rows={4}
          required
        />
      </div>

      {/* Preview */}
      {amountCents > 0 && (
        <>
          <Separator />
          
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Change Order Amount:</span>
                  <span className={`font-medium ${adjustmentType === 'credit' ? 'text-green-600' : 'text-orange-600'}`}>
                    {adjustmentType === 'credit' ? '−' : '+'} {formatCurrency(amountCents)}
                  </span>
                </div>
                
                {/* Vendor payout preview for debit adjustments */}
                {userRole === 'vendor' && adjustmentType === 'debit' && amountCents > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Platform Fee ({platformFeePercentage}%):
                      </span>
                      <span className="font-medium text-destructive">
                        − {formatCurrency(platformFeeCents)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm font-semibold text-primary">
                      <span>Your Additional Payout:</span>
                      <span>{formatCurrency(vendorPayoutCents)}</span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Approval required indicator */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This change order requires approval from the {userRole === 'client' ? 'vendor' : 'client'} before it will be applied.
              {adjustmentType === 'credit' && ' No payment will be required for this credit.'}
            </AlertDescription>
          </Alert>
        </>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={submitting || amountCents <= 0 || !reason.trim()}>
          {submitting ? 'Submitting...' : 'Submit for Approval'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
